import React, { useCallback, useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../../contexts/AuthContext';
import useErrorMessage from '../../../utils/ErrorMessage';
import { indicators } from '../../../utils/constants/misc';
import { dashboardReducer } from './dashboardReducer';
import { SimplePie } from './SimplePie';

const Indicator = ({ purpose }) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [stats, dispatchStats] = useReducer(dashboardReducer, {}, () => ({
    type: '',
    payload: undefined
  }));
  const { message } = useErrorMessage();

  const getStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/dashboards/${purpose}`
      });
      dispatchStats({ type: purpose, payload: data });
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
    // eslint-disable-next-line
  }, [purpose]);

  useEffect(() => {
    (async () => {
      await getStats();
    })();
    // eslint-disable-next-line
  }, [getStats]);

  return (
    <Card
      bordered={false}
      headStyle={{ padding: 0, height: 24, textAlign: 'right' }}
      styles={{ body: { padding: 8 } }}
      title={t(`dashboards.${purpose}.title`)}
      className="indicator-card"
    >
      <Row justify="start" style={{ marginBottom: 16 }} gutter={8}>
        <Skeleton loading={isLoading}>
          {(stats?.data ? stats.data.map((d) => d.item) : []).map((k) => (
            <Col span={8} key={k}>
              <Typography.Title level={4}>{stats?.[k] || 0}</Typography.Title>
              <Typography.Text>
                {t(`dashboards.${purpose}.${k}`)}
              </Typography.Text>
            </Col>
          ))}
        </Skeleton>
      </Row>
      <Row>
        <Col span={20}>
          <Skeleton loading={isLoading} paragraph={false}>
            <Typography.Title level={4}>
              {stats?.rate ? `${stats.rate}%` : '0%'}
            </Typography.Title>
            <Typography.Text>
              {`${t(`dashboards.${purpose}.rate`)}`}
            </Typography.Text>
          </Skeleton>
        </Col>
        <Col span={4} style={{ display: 'flex', alignItems: 'center' }}>
          <SimplePie data={stats?.data} />
        </Col>
      </Row>
    </Card>
  );
};

Indicator.propTypes = {
  purpose: PropTypes.oneOf(indicators).isRequired
};

export { Indicator };
